import { SITE_ORIGIN } from '../../lib/routeMeta'
import { kitchenPath } from '../../lib/slug'

/**
 * One kitchen link, one sentence, everywhere it is shared from.
 *
 * The public page and the chef's own screens each wrote their own share text,
 * and the public one interpolated an empty cuisine into "Check out Umar Isa's
 * kitchen on ChooseChow:  in Abuja, FCT". The URL also came from
 * window.location, which in the native shell is not a web address anyone can
 * open. The link is always the public origin plus the canonical slug — the same
 * URL the server's link-preview page answers on.
 */
export interface ShareableKitchen {
  id: string
  name: string
  cuisine?: string
  area?: string
}

export const kitchenUrl = (kitchen: Pick<ShareableKitchen, 'id' | 'name'>): string =>
  SITE_ORIGIN + kitchenPath(kitchen)

/** "Amara's Kitchen" already says kitchen; "Umar Isa" needs it added. */
const kitchenPhrase = (name: string): string =>
  /kitchen|cuisine|foods?\b|eatery|bakery|chops/i.test(name) ? name : `${name}’s kitchen`

/**
 * The message that travels with the link. `own` is the chef sharing their own
 * kitchen, so it speaks in the first person.
 */
export const kitchenShareText = (kitchen: ShareableKitchen, own = false): string => {
  const name = kitchen.name.trim()
  const cuisine = kitchen.cuisine?.trim() ?? ''
  // "Nigerian" is an adjective and needs "food"; "Nigerian dishes" does not.
  const food = !cuisine
    ? 'homemade food'
    : `homemade ${cuisine}${/food|dish|cuisine|meal|soup|snack|pastr|cake/i.test(cuisine) ? '' : ' food'}`
  const where = kitchen.area?.trim() ? ` in ${kitchen.area.trim()}` : ''
  if (own) return `I’m now cooking on ChooseChow! Order ${food} from ${name}${where}.`
  return `Order ${food} from ${kitchenPhrase(name)}${where} on ChooseChow.`
}

export type ShareOutcome = 'shared' | 'copied' | 'failed'

/** Opens the phone's share sheet, or copies the link where there is none. */
export const shareKitchen = async (kitchen: ShareableKitchen, own = false): Promise<ShareOutcome> => {
  const url = kitchenUrl(kitchen)
  const title = `${kitchen.name.trim()} on ChooseChow`
  const text = kitchenShareText(kitchen, own)
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title, text, url })
      return 'shared'
    } catch (error) {
      // Closing the sheet is a choice, not a failure; anything else falls back to copying.
      if (error instanceof DOMException && error.name === 'AbortError') return 'shared'
    }
  }
  return (await copyKitchenLink(kitchen)) ? 'copied' : 'failed'
}

export const copyKitchenLink = async (kitchen: Pick<ShareableKitchen, 'id' | 'name'>): Promise<boolean> => {
  if (typeof navigator === 'undefined' || !navigator.clipboard) return false
  try {
    await navigator.clipboard.writeText(kitchenUrl(kitchen))
    return true
  } catch {
    return false
  }
}
